import { Component } from 'preact';
import {
  GOLD,
  ACCENT_GOLD,
  TEXT_BODY,
  TEXT_PRIMARY,
  TEXT_MUTED,
  TEXTURED_PANEL_STYLE,
  SUB_PANEL_STYLE,
  CONTENT_BG_DEFAULT,
  FourCorners,
  GOLD_BTN,
} from '../../lib/medievalTheme';
import { pushDismiss, removeDismiss } from '../../utils/escStack';
import { t } from '../../localization/t';

const OPAQUE_PANEL_BG = '#0e0c08';
const OPAQUE_PANEL_STYLE = {
  ...TEXTURED_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, ${OPAQUE_PANEL_BG}`,
};
const OPAQUE_SUB_PANEL_STYLE = {
  ...SUB_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, ${OPAQUE_PANEL_BG}`,
};

const CLAMP_MIN = -25;
const CLAMP_MAX = 25;

const PANEL_WIDTH  = '16cqw';
const PANEL_HEIGHT = '14cqw';

export class HudLifeAdjustPanel extends Component {
  state = { delta: 0 };

  componentDidMount() {
    this._escDismissId = pushDismiss(() => this.props.onClose?.());
  }

  componentWillUnmount() {
    removeDismiss(this._escDismissId);
  }

  _step = (sign) => {
    this.setState((s) => ({ delta: Math.max(CLAMP_MIN, Math.min(CLAMP_MAX, s.delta + sign)) }));
  };

  _onInput = (e) => {
    const v = parseInt(e.currentTarget.value, 10);
    if (Number.isNaN(v)) { this.setState({ delta: 0 }); return; }
    this.setState({ delta: Math.max(CLAMP_MIN, Math.min(CLAMP_MAX, v)) });
  };

  _apply = () => {
    if (this.state.delta === 0) return;
    this.props.onAdjust?.(this.state.delta);
    this.setState({ delta: 0 });
    this.props.onClose?.();
  };

  _positionStyle = () => {
    const { canvasLocalPos } = this.props;
    if (!canvasLocalPos) return null;
    if (canvasLocalPos.edge === 'bottom') {
      return { bottom: `${canvasLocalPos.distFromCanvasBottom}px`, top: 'auto' };
    }
    return { top: `${canvasLocalPos.top}px`, bottom: 'auto' };
  };

  render({ currentLife, maxLife, canvasLocalPos, isReadOnly }) {
    if (isReadOnly || !canvasLocalPos) return null;
    const { delta } = this.state;
    const projected = Math.max(0, Math.min(maxLife, currentLife + delta));
    const canApply = delta !== 0;
    const deltaColor = delta > 0 ? '#7ec8ff' : delta < 0 ? '#ff8a78' : TEXT_PRIMARY;

    return (
      <div
        className="pointer-events-auto flex flex-col"
        style={{
          position: 'fixed',
          left: `${canvasLocalPos.left}px`,
          ...this._positionStyle(),
          transform: 'translateX(-100%)',
          marginLeft: '-0.5cqw',
          zIndex: 70,
          width: PANEL_WIDTH,
          height: PANEL_HEIGHT,
          padding: '0.9cqw',
          gap: '0.6cqw',
          ...OPAQUE_PANEL_STYLE,
        }}
        data-buff-zone="panel"
        onClick={(e) => e.stopPropagation()}
      >
        <FourCorners color={`${GOLD} 0.55)`} />

 <div
 className="flex-1 flex flex-col items-center justify-center"
 style={{
 ...OPAQUE_SUB_PANEL_STYLE,
 minHeight: 0,
 padding: '0.6cqw',
 gap: '0.35cqw',
 }}
 >
 <div
 className="arena-heading"
 style={{
 color: ACCENT_GOLD,
 fontSize: '0.65cqw',
 fontWeight: 700,
 }}
 >
 {t('HP')}
 </div>
 <div className="flex items-baseline tabular-nums" style={{ gap: '0.3cqw' }}>
 <span style={{ color: TEXT_PRIMARY, fontSize: '1.8cqw', fontWeight: 800, lineHeight: 1 }}>{currentLife}</span>
 <span style={{ color: TEXT_MUTED, fontSize: '0.8cqw' }}>/ {maxLife}</span>
 </div>
 {delta !== 0 ? (
 <div className="flex items-center tabular-nums" style={{ gap: '0.35cqw', marginTop: '0.25cqw' }}>
 <span style={{ color: TEXT_MUTED, fontSize: '0.8cqw' }}>→</span>
 <span style={{ color: deltaColor, fontSize: '1.2cqw', fontWeight: 800, lineHeight: 1 }}>{projected}</span>
 </div>
 ) : null}
 </div>

 <div
 style={{
 ...OPAQUE_SUB_PANEL_STYLE,
 padding: '0.6cqw',
 display: 'flex',
 flexDirection: 'column',
 gap: '0.5cqw',
 flexShrink: 0,
 }}
 >
 <div className="flex items-center justify-center" style={{ gap: '0.4cqw' }}>
 <StepBtn onClick={() => this._step(-1)} label="−" />
 <input
 type="number"
 value={delta}
 onInput={this._onInput}
 className="text-center tabular-nums"
 style={{
 width: '3.2cqw',
 height: '2cqw',
 borderRadius: 'var(--radius-sm)',
 background: 'rgba(0,0,0,0.5)',
 border: `1px solid ${GOLD} 0.3)`,
                color: deltaColor,
                fontWeight: 800,
                fontSize: '1.05cqw',
                outline: 'none',
              }}
            />
            <StepBtn onClick={() => this._step(1)} label="+" />
          </div>
          <button
            type="button"
            className="arena-heading tracking-wider vh-medieval-btn-flat"
            onClick={this._apply}
            disabled={!canApply}
            style={{
              ...GOLD_BTN,
              width: '100%',
              padding: '0.5cqw',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.78cqw',
              opacity: canApply ? 1 : 0.4,
            }}
          >
            {t('Apply')}
          </button>
        </div>
      </div>
    );
  }
}

function StepBtn({ onClick, label }) {
  return (
    <button
      type="button"
      className="vh-medieval-btn"
      onClick={onClick}
      style={{
        ...GOLD_BTN,
        width: '2cqw',
        height: '2cqw',
        borderRadius: 'var(--radius-sm)',
        fontSize: '1.05cqw',
        fontWeight: 800,
        lineHeight: 1,
        padding: 0,
      }}
      aria-label={label === '+' ? t('Increase') : t('Decrease')}
    >
      {label}
    </button>
  );
}
